import { DatePipe } from '@angular/common';
import { Component, inject, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, NavigationSkipped, Params, Router, RouterModule, RouterOutlet } from '@angular/router';
import { CsiNgBrandHeaderComponent } from '@csi-ng-components/csi-ng-brand-header';
import { CsiNgBrandFooterComponent } from '@csi-ng-components/csi-ng-brand-footer';
import { CommunityAuthService } from './services/community-auth.service';
import { combineLatest, debounceTime, delay, distinctUntilChanged, filter, pairwise, Subscription, switchMap, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SessionExpiredComponent } from './dialogs/session-expired/session-expired.component';
import { UsersService } from './services/user.service';
import { environment, OAuthConfig } from '../environments/environment';
import { startOfWeek } from 'date-fns';
import { User } from './models/user';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ThemeService } from '../assets/theme/_theme.service';
import { DefaultQueries } from './services/default-queries.service';

export interface TabRoute {
  routePart: string;
  label: string;
  class?: string;
  icon?: string;
  isAvailable: () => boolean;
}

@Pipe({
  name: 'isTabAvailable'
})
export class IsTabAvailablePipe implements PipeTransform {
  transform(items: TabRoute[], changeIndex: number): any[] {
    if (!items) {
      return items;
    }
    return items.filter(item => item.isAvailable());
  }
}

export const testWarningKey = "hideTestWarning";

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatTabsModule,
    MatToolbarModule,
    MatButtonModule,
    RouterModule,
    MatIconModule,
    CsiNgBrandHeaderComponent,
    CsiNgBrandFooterComponent,
    MatDialogModule,
    IsTabAvailablePipe,
    MatSlideToggleModule,
    FormsModule
],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  @ViewChild(MatTabGroup) tabs?: MatTabGroup;
  userService = inject(UsersService);
  datepipe = inject(DatePipe);
  selectedTab = 0;
  tabRoutes: TabRoute[] = [
    { routePart: "timesheet", label: "Timesheet", isAvailable: () => true },
    { routePart: "efforts", label: "Efforts", isAvailable: (() => this.isPrivileged).bind(this) },
    { routePart: "projects", label: "Projects", isAvailable: () => this.isPrivileged },
    { routePart: "departments", label: "Departments", isAvailable: () => this.isPrivileged },
    { routePart: "users", label: "Users", isAvailable: () => this.isPrivileged },
    { routePart: "submissions", label: "Submissions", isAvailable: () => this.isReportTo },
    { routePart: "feedback", label: "Feedback", isAvailable: () => true, class: "feedback", icon: 'feedback' },
    { routePart: "settings", label: "Settings", isAvailable: () => true }
  ];
  defaultFilters: { [tabKey: string]: Params } = {
    "submissions": { "filter.status": "submitted", "filter.reportsto.hawkid" : null, "filter.start": null, "filter.end": null },
    "users": { "filter.reportsto.hawkid" : null }
  };
  title = 'time-tracking';
  latestRoute = '';
  showToolbar = false;
  bttnType: string | null = null;
  user = { id: '', userName: '' };
  isReportTo = false;
  isPrivileged = false;
  isValidUser = false;
  changeIndex: number = 0;
  previousTab: number = 0;
  tksUser?: User;
  isTestEnvironment: boolean = !environment.production;
  isTestWarningHidden = false;
  get isImpersonating() { return this.authService.isImpersonating; }
  isDarkMode = true;
  private themeSub?: Subscription;

  constructor(
    public authService: CommunityAuthService, 
    private router: Router, 
    private dialog: MatDialog,
    private themeService: ThemeService,
    private defaultQueries: DefaultQueries
  ) {
    this.isDarkMode = themeService.darkModeGuess;
    console.log("guess", themeService.darkModeGuess);
    this.router.events.pipe(debounceTime(50)).subscribe(event => {
      console.log(event);
      if (event instanceof NavigationEnd) {
        this.setTab(event.urlAfterRedirects);
      }
    });
    this.authService.isAuthenticated$.pipe(debounceTime(50)).subscribe(_ => {
      const nav = router.getCurrentNavigation();
      if (nav?.finalUrl) this.setTab(nav.finalUrl.toString());
    });
    combineLatest([this.authService.isImpersonated, this.userService.preferencesUpdated$])
      .pipe(
        switchMap(_ => this.authService.isAuthenticated$),
        filter(isAuth => isAuth),
        switchMap(_ => this.userService.loadCurrentUser()))
      .subscribe({ next: user => {
        this.tksUser = user;
        this.user = { userName: user.hawkId, id: user.hawkId };
        this.bttnType = 'logout';
        this.showToolbar = true;
        this.isValidUser = !!user;
        this.isPrivileged = user.userRoles.find(ur => ["admin", "fullaccess"].find(r => r == ur?.role?.roleName?.toLowerCase())) != null;
        this.isReportTo = !!user.reports?.length;
        this.defaultFilters = defaultQueries.compute(user);
        this.changeIndex ++;
      } } );

    this.authService.isAuthenticated$.pipe(
      delay(13), // enough to prevent "Expression has changed after it was checked."
      distinctUntilChanged(),
      tap(this.checkAuth.bind(this))
    ).subscribe();
    
    authService.isAuthenticated$.pipe(
      pairwise(),
      filter(([a, b]) => a && !b),
      tap(this._sessionExpired.bind(this)))
      .subscribe();
    
    this.isTestWarningHidden = localStorage[testWarningKey] === 'true';
    // this.authService.isProfileLoaded$.pipe(filter(i => i), delay(23), tap(this.checkAuth.bind(this))).subscribe();
  }

  ngOnInit(): void {
    this.themeSub = this.themeService.isDarkMode$.subscribe(dark => {
      this.isDarkMode = dark;
    })
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  setTab(url: string) {
    this.latestRoute = url;
    const match = /\/(\w+)/.exec(this.latestRoute);
    if (match) {
      //console.log("Setting tab to", match[1], this.tabRoutes.indexOf(match[1]))
      console.log(this.tabs?._tabs.toArray());
      const tab = this.tabRoutes.filter(tr => tr.isAvailable()).findIndex(tr => tr.routePart == match[1]);
      this.previousTab = this.selectedTab = tab;
    } else {
      console.log("Did not match", this.latestRoute);
    }
  }
  
  toggleDarkMode(): void {
    // const newValue = !this.isDarkMode;         
    // this.isDarkMode = newValue;             
    // this.themeService.toggleTheme(newValue); 
    this.themeService.toggleTheme(!this.isDarkMode); 
  }

  endImpersonation() { this.authService.endImpersonation(); }

  toggleMinimizeTestWarning() {
    this.isTestWarningHidden = !(localStorage[testWarningKey] === 'true');
    localStorage.setItem(testWarningKey, ''+this.isTestWarningHidden);
  }

  ngAfterViewInit() {
    this.updateTabChoice();
  }

  updateTabChoice() {

  }

  private _sessionExpired() {
    this.dialog.open(SessionExpiredComponent, { disableClose: true });
  }

  async getUser() {
    try{
        console.log(this.authService.profile);
    }
    catch(e){
        console.error('error getting user', e);
    }
}

  async onTabChange(event: any) {
    //const tab = this.tabRoutes[event.index];
    const tab = this.tabRoutes.filter(tr => tr.isAvailable())[event.index].routePart;
    const params = this.defaultFilters[tab] ?? '';
    if (!this.latestRoute.startsWith('/' + tab)) {
      const success = await this.router.navigate(['/' + tab], { queryParams: params });
      if (success) {
        this.previousTab = event.index;
        console.log('Successfully changed tab');
        return;
      } else {
        console.log('Reverted tab change', this.previousTab);
        if (this.previousTab !== undefined)
          this.tabs!.selectedIndex = this.previousTab;
      }
    }
  }

  stopImpersonation() {
    this.authService.endImpersonation();
  }

  checkAuth() {
    // console.log('authenticated', this.authenticated);
    if (!this.authService.isAuthenticated()) {
      this.user = { userName: '', id: '' };
      this.bttnType = 'login';
      this.showToolbar = false;
    }
  }

  async receiveFromHeader(message: string) {
    console.log(message);
    if (message === 'logout') {
      this.authService.logout();
    } else if (message === 'login') {
      console.log("Logging in");
      this.authService.login(window.location.href, true);
    }
  }

  receiveFromFooter(bogus: any) {

  }

  siteName = {
    id: '1',
    siteName: 'Time Keeping System (TKS)',
    siteUrl: OAuthConfig.redirectUri ?? window.location.href
  }

  siteFooter = {
    id: '1',
    siteName: 'Information Technology Services',
    siteUrl: 'https://csi.its.uiowa.edu/',
    parentSiteName: '',
    parentSiteUrl: 'https://uiowa.edu/',
    address1: 'Information Technology Services (ITS)',
    address2: '2800 University Capitol Centre',
    city: 'Iowa City',
    state: 'Iowa',
    zip: '52242',
    phone: '319-384-4357',
    email: 'its-helpdesk@uiowa.edu',
    facebookUrl: 'https://www.facebook.com/itsuiowa',
    xUrl: 'https://x.com/itsuiowa',
    igUrl: '',
    emailSubject: 'Regarding the ITS Time Tracker application'
  }
}
